import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const resetToken = params.get("token");
  const [email, setEmail] = useState("");
  const [token, setTokenVal] = useState(resetToken || "");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isReset = !!resetToken;

  const sendLink = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setMsg("");
    try {
      await api.forgotPassword(email);
      setMsg("If that email exists, a reset link has been sent. Please check your inbox.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setMsg("");
    try {
      await api.resetPassword(token, password);
      setMsg("Password reset! You can now log in.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <AuthShell title={isReset ? "Reset password" : "Forgot password"} subtitle={isReset ? "Enter your new password" : "We'll generate a reset link"}>
      {error && <p className="text-sm text-destructive mb-3" data-testid="forgot-error">{error}</p>}
      {msg && <p className="text-sm text-accent mb-3" data-testid="forgot-msg">{msg}</p>}
      {isReset ? (
        <form onSubmit={doReset} className="space-y-4">
          <div className="space-y-2">
            <Label>Reset token</Label>
            <Input value={token} onChange={(e) => setTokenVal(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type="password" data-testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="reset-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reset password
          </Button>
        </form>
      ) : (
        <form onSubmit={sendLink} className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="forgot-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send reset link
          </Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-muted-foreground">
        <Link to="/login" className="text-accent hover:underline font-medium">Back to login</Link>
      </p>
    </AuthShell>
  );
}
